// Client-side PDF generation using html2canvas and jsPDF
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { currencySymbolMap } from './currencies';
import { globalDateFormat } from './helper';

// Import the base64 encoded images
import Logo from '../assets/img/logo/logo.svg';
import paidIcon from '../assets/img/icons/paid-icon.svg';

const generateInvoiceHTML = (invoiceDetails, selectedOrganization) => {
  const {
    invoice_number,
    currency,
    client,
    client_name,
    issued_date,
    due_date,
    discounted_amount,
    paid_amount,
    total_amount,
    items,
    tax1,
    tax2,
    discount,
    status,
    total_tax1,
    total_tax2,
  } = invoiceDetails;

  // Calculate total
  let totalCalc = 0;
  if (items && items.length) {
    items.forEach(item => {
      totalCalc += parseFloat(item.quantity) * parseFloat(item.unit_price);
    });
  }

  const discountAmount = parseFloat(total_amount) - parseFloat(discounted_amount);
  const amountDue = Math.max(parseFloat(discounted_amount) - parseFloat(paid_amount), 0);

  return `
    <style>
      * {
        margin: 0;
        padding: 0;
        box-sizing: border-box;
      }
      
      body {
        font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        line-height: 1.4;
        color: #333;
        background: #fff;
      }
      
      .invoice-container {
        max-width: 800px;
        margin: 0 auto;
        background: #fff;
        border: 1px solid #ddd;
        padding: 24px;
      }
      
      .header {
        padding: 16px;
        background-color: #EDF8FF;
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
      }
      
      .org-info {
        display: flex;
        flex-direction: column;
        gap: 12px;
      }
      
      .org-header {
        display: flex;
        align-items: center;
        gap: 8px;
      }
      
      .org-logo {
        width: 32px;
        height: 32px;
      }
      
      .org-name {
        font-size: 20px;
        font-weight: bold;
        margin: 0;
      }
      
      .org-details {
        display: flex;
        flex-direction: column;
        gap: 8px;
      }
      
      .org-detail {
        font-size: 13px;
        color: #555555;
        margin: 0;
      }
      
      .invoice-title {
        margin: 0;
        font-size: 32px;
        font-weight: bold;
        color: #2266EF;
      }
      
      .dashed-divider {
        border-top: 2px dashed #C6D3D1;
        margin: 0;
      }
      
      .invoice-info {
        display: grid;
        grid-template-columns: 2fr 1fr 1fr 1fr;
        gap: 30px;
        padding: 16px;
      }
      
      .info-section {
        display: flex;
        flex-direction: column;
        gap: 4px;
      }
      
      .info-label {
        font-size: 14px;
        font-weight: normal;
        margin: 0;
      }
      
      .info-value {
        font-size: 16px;
        font-weight: bold;
        margin: 0;
      }
      
      .info-value-sm {
        font-size: 14px;
        font-weight: bold;
        margin: 0;
      }
      
      .items-section {
        padding: 16px;
      }
      
      .items-header {
        display: grid;
        grid-template-columns: 40px 80px 2fr 80px 100px 100px;
        gap: 12px;
        padding: 8px 0;
        border-bottom: 1px solid #C6D3D1;
        font-weight: normal;
        font-size: 14px;
      }
      
      .items-row {
        display: grid;
        grid-template-columns: 40px 80px 2fr 80px 100px 100px;
        gap: 12px;
        padding: 8px 0;
        font-size: 14px;
      }
      
      .divider {
        border-top: 0.5px solid #C6D3D1;
        margin: 0;
      }
      
      .totals-section {
        position: relative;
        padding: 16px;
        display: grid;
        grid-template-columns: 1fr auto;
        gap: 12px;
        align-items: center;
      }
      
      .paid-watermark {
        position: absolute;
        left: 100px;
        top: 15%;
        width: 80px;
        opacity: 0.7;
      }
      
      .total-label {
        font-size: 14px;
        text-align: right;
      }
      
      .total-value {
        font-size: 14px;
        font-weight: bold;
        text-align: right;
      }
      
      .amount-due {
        background-color: #EDF8FE;
        padding: 8px 16px;
        display: grid;
        grid-template-columns: auto auto;
        gap: 10px;
        justify-content: end;
        align-items: center;
      }
      
      .amount-due-label {
        font-size: 13px;
      }
      
      .amount-due-value {
        font-weight: bold;
        font-size: 13px;
      }

      .signature-section {
        display: grid;
        grid-template-columns: 150px 150px;
        justify-content: start;
        align-content: flex-end;
        grid-gap: 150px;
        margin-top: 100px;
        padding: 0 80px ;
      }
      
      .signature-block {
        display: grid;
        grid-template-columns: 1fr;
        align-content: flex-end;
        gap: 8px;
        padding: 0;
        height: 30px;
        margin: 0;
      }
      
      .signature-image {
        max-width: 150px;
        max-height: 40px;
        justify-self: center;
      }
      
      .signature-line {
        width: 150px;
        border-top: 1px solid #333;
        margin: 0;
      }
      
      .signature-label {
        font-size: 13px;
        margin: 0;
      }
      
      .date-value {
        font-size: 13px;
        margin: 0;
      }
      
      .footer {
        display: grid;
        grid-template-columns: auto auto;
        justify-content: space-between;
        align-items: center;
        padding: 12px 16px;
        background-color: #EDF8FE;
        margin-top: 50px;
      }
      
      .footer-text {
        font-size: 13px;
        margin: 0;
      }
      
      .footer-logo {
        width: 100px;
      }
      
      .text-center {
        text-align: center;
      }
      
      .text-right {
        text-align: right;
      }
      
      .bold {
        font-weight: bold;
      }
    </style>
    
    <div class="invoice-container">
      <!-- Header Section -->
      <div class="header">
        <div class="org-info">
          <div class="org-header">
            ${selectedOrganization?.organization_logo_base64 ? `<img src="${selectedOrganization.organization_logo_base64}" class="org-logo" alt="Logo" />` : ''}
            <h2 class="org-name">${selectedOrganization?.name || ''}</h2>
          </div>
          <div class="org-details">
            <p class="org-detail">${selectedOrganization?.address || ''}</p>
            <p class="org-detail">${selectedOrganization?.email || ''}</p>
            <p class="org-detail">${selectedOrganization?.phone || ''}</p>
          </div>
        </div>
        <h1 class="invoice-title">INVOICE</h1>
      </div>
      
      <hr class="dashed-divider" />
      
      <!-- Invoice Info Section -->
      <div class="invoice-info">
        <div class="info-section">
          <p class="info-label">Billed to</p>
          <p class="info-value">${client.name}</p>
          <p class="info-label">${client.email}</p>
        </div>
        
        <div class="info-section">
          <p class="info-label">Issue Date</p>
          <p class="info-value-sm">${globalDateFormat(issued_date)}</p>
        </div>
        
        <div class="info-section">
          <p class="info-label">Due Date</p>
          <p class="info-value-sm">${due_date ? globalDateFormat(due_date) : '-'}</p>
        </div>
        
        <div class="info-section">
          <p class="info-label">Invoice no</p>
          <p class="info-value-sm">${invoice_number}</p>
        </div>
      </div>
      
      <hr class="dashed-divider" />
      
      <!-- Items Section -->
      <div class="items-section">
        <div class="items-header">
          <span>#</span>
          <span>Type</span>
          <span>Item / Description</span>
          <span>Quantity</span>
          <span>Unit Price</span>
          <span>Amount</span>
        </div>
        
        <hr class="divider" />
        
        ${items.map((item, index) => `
          <div class="items-row">
            <span>${index + 1}.</span>
            <span>${item.type}</span>
            <span>${item.description}</span>
            <span>${item.quantity}</span>
            <span>${parseFloat(item.unit_price).toFixed(2)}</span>
            <span>${(parseFloat(item.quantity) * parseFloat(item.unit_price)).toFixed(2)}</span>
          </div>
        `).join('')}
        
        <hr class="divider" />
        
        <!-- Totals Section -->
        <div class="totals-section">
          ${status === 'paid' ? `<img src="${paidIcon}" class="paid-watermark" alt="Paid" />` : ''}
          
          <span class="total-label">Subtotal</span>
          <span class="total-value">${currencySymbolMap[currency]} ${totalCalc.toFixed(2)}</span>
          
          ${(parseFloat(discount.percent_amount) > 0 || parseFloat(discount.fixed_amount) > 0) ? `
            <span class="total-label">
              ${discount.type === 'percent' 
                ? `Discount (${parseFloat(discount.percent_amount).toFixed(2)}%)`
                : `Discount (${currencySymbolMap[currency]} ${parseFloat(discount.fixed_amount).toFixed(2)})`
              }
            </span>
            <span class="total-value">
              - ${currencySymbolMap[currency]} ${discount.type === 'percent' 
                ? discountAmount.toFixed(2)
                : parseFloat(discount.fixed_amount).toFixed(2)
              }
            </span>
          ` : ''}
          
          ${(parseFloat(tax1.percent_amount) > 0 || parseFloat(tax1.fixed_amount) > 0) ? `
            <span class="total-label">
              ${tax1.name.toLowerCase() !== 'tax 1' ? 'Tax 1 - ' : ''}
              ${tax1.type === 'percent' 
                ? `${tax1.name} (${parseFloat(tax1.percent_amount).toFixed(2)}%)`
                : `${tax1.name} (${currencySymbolMap[currency]} ${parseFloat(tax1.fixed_amount).toFixed(2)})`
              }
            </span>
            <span class="total-value">${currencySymbolMap[currency]} ${total_tax1.toFixed(2)}</span>
          ` : ''}
          
          ${tax2 && (parseFloat(tax2.percent_amount) > 0 || parseFloat(tax2.fixed_amount) > 0) ? `
            <span class="total-label">
              ${tax2.name.toLowerCase() !== 'tax 2' ? 'Tax 2 - ' : ''}
              ${tax2.type === 'percent' 
                ? `${tax2.name} (${parseFloat(tax2.percent_amount).toFixed(2)}%)`
                : `${tax2.name} (${currencySymbolMap[currency]} ${parseFloat(tax2.fixed_amount).toFixed(2)})`
              }
            </span>
            <span class="total-value">${currencySymbolMap[currency]} ${total_tax2.toFixed(2)}</span>
          ` : ''}
          
          <span class="total-label">Grand Total</span>
          <span class="total-value">${currencySymbolMap[currency]} ${parseFloat(discounted_amount).toFixed(2)}</span>
          
          <span class="total-label">Paid</span>
          <span class="total-value">${currencySymbolMap[currency]} ${parseFloat(paid_amount).toFixed(2)}</span>
        </div>
        
        <div class="amount-due">
          <span class="amount-due-label">Amount Due</span>
          <span class="amount-due-value">${currencySymbolMap[currency]} ${amountDue.toFixed(2)}</span>
        </div>
      </div>


      <!-- Signature Section -->
      <div class="signature-section">
       
        <div class="signature-block">
          ${selectedOrganization?.digital_signature_base64 ? `<img src="${selectedOrganization.digital_signature_base64}" class="signature-image" alt="Digital Signature" />` : ''}
          <hr class="signature-line" />
          <p class="signature-label">Signature</p>
        </div>

        <div class="signature-block">
          <p class="date-value">${globalDateFormat(invoiceDetails.issued_date)}</p>
          <hr class="signature-line" />
          <p class="signature-label">Date</p>
        </div>
      </div>
      
      <!-- Footer -->
      <div class="footer">
        <p class="footer-text">Powered by</p>
        <div>
            <img src="${Logo}" class="footer-logo" alt="SpaceSoft Logo" />
        </div>
      </div>
    </div>
  `;
};

export const generateInvoicePDF = async (invoiceDetails, selectedOrganization, options = {}) => {
  try {
    // Create a temporary container in the DOM
    const tempContainer = document.createElement('div');
    tempContainer.style.position = 'absolute';
    tempContainer.style.left = '-9999px';
    tempContainer.style.top = '-9999px';
    tempContainer.style.width = '210mm'; // A4 width
    tempContainer.style.background = 'white';
    
    // Generate HTML content
    const htmlContent = generateInvoiceHTML(invoiceDetails, selectedOrganization);
    tempContainer.innerHTML = htmlContent;
    
    // Add to DOM
    document.body.appendChild(tempContainer);
    
    // Generate canvas from HTML
    const canvas = await html2canvas(tempContainer, {
      width: 794, // A4 width in pixels at 96 DPI
      height: 1123, // A4 height in pixels at 96 DPI
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff'
    });
    
    // Remove temporary container
    document.body.removeChild(tempContainer);
    
    // Create PDF
    const pdf = new jsPDF('p', 'mm', 'a4');
    const imgData = canvas.toDataURL('image/png');
    
    // Calculate dimensions to fit A4
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = canvas.width;
    const imgHeight = canvas.height;
    const ratio = Math.min(pdfWidth / (imgWidth * 0.264583), pdfHeight / (imgHeight * 0.264583));
    
    const width = imgWidth * 0.264583 * ratio;
    const height = imgHeight * 0.264583 * ratio;
    
    pdf.addImage(imgData, 'PNG', 0, 0, width, height);
    
    return pdf.output('arraybuffer');
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw error;
  }
};

// Function to download PDF
export const downloadInvoicePDF = async (invoiceDetails, selectedOrganization, filename) => {
  try {
    const pdfArrayBuffer = await generateInvoicePDF(invoiceDetails, selectedOrganization);
    
    // Create blob and download
    const blob = new Blob([pdfArrayBuffer], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || `invoice-${invoiceDetails.invoice_number}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Clean up
    URL.revokeObjectURL(url);
    
    return true;
  } catch (error) {
    console.error('Error downloading PDF:', error);
    return false;
  }
};

// Function to get PDF as base64
export const getInvoicePDFAsBase64 = async (invoiceDetails, selectedOrganization) => {
  try {
    const pdfArrayBuffer = await generateInvoicePDF(invoiceDetails, selectedOrganization);
    const uint8Array = new Uint8Array(pdfArrayBuffer);
    let binaryString = '';
    for (let i = 0; i < uint8Array.length; i++) {
      binaryString += String.fromCharCode(uint8Array[i]);
    }
    return btoa(binaryString);
  } catch (error) {
    console.error('Error generating PDF as base64:', error);
    throw error;
  }
};

// Function to print PDF directly
export const printInvoicePDF = async (invoiceDetails, selectedOrganization) => {
  try {
    const pdfArrayBuffer = await generateInvoicePDF(invoiceDetails, selectedOrganization);
    const blob = new Blob([pdfArrayBuffer], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    
    // Open PDF in new tab for printing
    const printWindow = window.open(url, '_blank');
    
    if (printWindow) {
      // Wait for PDF to load before triggering print
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
        }, 500);
      };
      
      // Clean up after some time
      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 10000);
      
      return true;
    } else {
      throw new Error('Unable to open print window. Please check if popup blocker is enabled.');
    }
  } catch (error) {
    console.error('Error printing PDF:', error);
    throw error;
  }
};